<template>
  <div>
    <app-grid
      v-if="profile.subscription"
      :items-count="countSubscribedProjectTypes"
    >
      <template
        v-if="profile.subscription &&
        !profile.subscription?.hideProjectOption &&
        profile.isSubUser"
      >
        <ProjectTypeProject />
      </template>
      <template
        v-if="profile.subscription &&
         !profile.subscription?.hideDataroomOption &&
         profile.isSubUser"
      >
        <ProjectTypeDataroom />
      </template>
      <template
        v-if="profile.subscription &&
        !profile.subscription?.hideArchiveOption"
      >
        <ProjectTypeArchive />
      </template>
    </app-grid>

    <div class="my-9">
      <DocumentsBreadcrumb v-if="!this.projectsFolderByIdPending" :items="getProjectsFoldersPath" />
      <v-skeleton-loader v-else width="260px" type="text" />
    </div>

    <div class="d-flex align-center column-gap-4">
      <SearchTextField
        v-model="search"
        :tooltipText="$t('projects.ProjectsHeader.searchPlaceholder')"
        :loading="searchModeEnabled && (pmRoomsPending || guestRoomsPending)"
      />

      <app-button
        type="outlined"
        variant="neutral"
        @click="$store.commit('rooms/OPEN_FILTER_PANE')"
      >
        <v-icon :start="!$vuetify.display.xs" size="14">fa fa-sliders-h</v-icon>
        <span v-if="!$vuetify.display.xs">{{$t('projects.ProjectsHeader.filters')}}</span>
      </app-button>

      <div class="d-flex align-center ml-auto column-gap-4">
        <app-button v-if="(!currentProjectsFolder.id || currentProjectsFolder.depth < FOLDER_MAX_DEPTH) && !isRecentRoomsView && !searchModeEnabled"
          class="projects-header__create-new-folder"
          :class="{'ml-auto': !$vuetify.display.xs}"
          @click="openProjectsCreateNewFolder"
        >
          {{ $t('project.documents.dialogs.DocumentsNewFolderDialog.createFolder') }}
        </app-button>

        <div v-if="!$vuetify.display.mobile" class="d-flex align-center column-gap-2">
          <AppTooltip>
            <template #activator="{ props }">
              <app-button
                type="icon"
                v-bind="props"
                @click="emitDisplayChoice(DISPLAY.grid)"
              >
                <font-awesome-icon
                  :icon="['far', 'border-all']"
                  class="project-header-icon"
                  :class="{'text-accent-lighten-2': display === DISPLAY.table }"
                ></font-awesome-icon>
              </app-button>
            </template>
            <span>{{$t('projects.ProjectsHeader.gridDisplay')}}</span>
          </AppTooltip>
          <AppTooltip>
            <template v-slot:activator="{ props }">
              <app-button
                type="icon"
                v-bind="props"
                @click="emitDisplayChoice(DISPLAY.table)"
              >
                <font-awesome-icon
                  :icon="['far', 'list']"
                  class="project-header-icon"
                  :class="{'text-accent-lighten-2': display === DISPLAY.grid }"
                ></font-awesome-icon>
              </app-button>
            </template>
            <span>{{$t('projects.ProjectsHeader.tableDisplay')}}</span>
          </AppTooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'

import AppTooltip from '@/common/AppTooltip.vue'
import { FOLDER_MAX_DEPTH } from '@/common/constants'
import SearchTextField from '@/common/SearchTextField.vue'
import DocumentsBreadcrumb from '@/project/documents/DocumentsBreadcrumb.vue'
import { SET_IS_OPEN } from '@/store/modules/projects-create-new-folder/mutation_types'
import { SET_DISPLAY_TYPE } from '@/store/modules/rooms/mutation_types'

import { DISPLAY } from './display'
import ProjectTypeArchive from './project-types/ProjectTypeArchive.vue'
import ProjectTypeDataroom from './project-types/ProjectTypeDataroom.vue'
import ProjectTypeProject from './project-types/ProjectTypeProject.vue'

export default {
  name: 'ProjectsHeader',
  components: {
    AppTooltip,
    SearchTextField,
    ProjectTypeProject,
    ProjectTypeDataroom,
    ProjectTypeArchive,
    DocumentsBreadcrumb,
  },
  props: {
    display: {
      required: true,
      validator: value => { return value in DISPLAY },
    },
    searchQuery: {
      type: String,
      required: true,
    },
    isRecentRoomsView: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      DISPLAY,
      FOLDER_MAX_DEPTH,
    }
  },
  computed: {
    ...mapState('projectsFolders', ['currentProjectsFolder']),
    ...mapState('rooms', ['sort', 'displayType', 'searchModeEnabled', 'pmRoomsPending', 'guestRoomsPending']),
    ...mapState('user', ['profile']),
    ...mapState('projectsFolders', ['projectsFoldersTree', 'currentProjectsFolder', 'projectsFolderByIdPending']),
    getProjectsFoldersPath () {
      const path = this.findProjectsFolderPath(this.projectsFoldersTree, this.currentProjectsFolder.parentId)
      let allPath = []
      if (this.isRecentRoomsView) {
        allPath = [{
          id: 'recent',
          name: this.$t('projects.ProjectsHeader.recent'),
        }]
      } else {
        allPath = [
          {
            id: undefined,
            name: this.$t('projects.ProjectsHeader.myProjects'),
          },
          ...path,
        ]
        if (this.currentProjectsFolder.id) {
          allPath.push(this.currentProjectsFolder)
        }
      }
      return allPath
    },
    search: {
      get () {
        return this.searchQuery
      },
      set (val) {
        this.$emit('update:searchQuery', val)
      },
    },
    countSubscribedProjectTypes () {
      let count = 0
      if (this.profile.subscription) {
        if (!this.profile.subscription?.hideProjectOption && this.profile.isSubUser) {
          count++
        }
        if (!this.profile.subscription?.hideDataroomOption && this.profile.isSubUser) {
          count++
        }
        if (!this.profile.subscription?.hideArchiveOption) {
          count++
        }
      }
      return count
    },
  },
  methods: {
    ...mapMutations('rooms', [SET_DISPLAY_TYPE]),
    ...mapMutations('projectsCreateNewFolder', [SET_IS_OPEN]),
    openProjectsCreateNewFolder () {
      this.SET_IS_OPEN(true)
    },
    emitDisplayChoice (displayType) {
      this.SET_DISPLAY_TYPE(displayType)
      this.$emit('display', displayType)
    },
    findProjectsFolderPath (dossiers, parentId, path = []) {
      const parent = dossiers.find(dossier => dossier.id === parentId)
      if (!parent) {
        // If the parent folder is not found, returns an empty list
        return path
      }
      path.unshift(parent)

      // Otherwise, recursively searches for the parent folder
      return this.findProjectsFolderPath(dossiers, parent.parentId, path)
    },
  },
}
</script>

<style scoped>
.project-header-icon {
  font-size: 24px;
}
</style>
